import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsInDraftMode } from './useDraftMode';
import {
  gitService,
  TERMINAL_JOB_STATUSES,
  type DevelopCommitsPage,
  type DevelopGitInfo,
  type GithubRepoOption,
  type ImportRun,
  type PushJobStatus,
  type SourceRepoConfig,
  type SourceRepoUpdate,
} from '../services/git.service';

/**
 * The tenant's repo + the head of the branch backing the CURRENT Pliable branch.
 *
 * Keyed on the mode. Both endpoints answer a different question in Develop than in
 * Production — which git branch, which head, which sync marker — and the mode
 * travels as a request header, not a URL parameter. Without it in the key, React
 * Query serves the Develop answer after switching to Production and never re-asks.
 */
export function useDevelopInfo() {
  const isInDraftMode = useIsInDraftMode();
  return useQuery<DevelopGitInfo>({
    queryKey: ['git-develop', isInDraftMode ? 'DRAFT' : 'PROD'],
    queryFn: () => gitService.getDevelop(),
    staleTime: 15_000,
  });
}

/** Kicks the background export→push job (Develop → git); returns the job id to poll. */
export function usePushDevelop() {
  return useMutation({
    mutationFn: () => gitService.pushDevelop(),
  });
}

/** Kicks the background import job (git → Develop); returns the job id to poll. */
export function usePullDevelop() {
  return useMutation({
    mutationFn: () => gitService.pullDevelop(),
  });
}

/** Kicks the background import job (git → Production); returns the job id to poll. */
export function usePullProd() {
  return useMutation({
    mutationFn: () => gitService.pullProd(),
  });
}

/** A page of commits on the branch backing the current Pliable branch. */
export function useDevelopCommits(page: number, perPage = 20) {
  const isInDraftMode = useIsInDraftMode();
  return useQuery<DevelopCommitsPage>({
    queryKey: ['git-develop-commits', isInDraftMode ? 'DRAFT' : 'PROD', page, perPage],
    queryFn: () => gitService.getDevelopCommits(page, perPage),
    staleTime: 15_000,
  });
}

/** Polls a push/pull job until it reaches a terminal status. */
export function useGitJobStatus(jobId: string | null) {
  return useQuery<PushJobStatus>({
    queryKey: ['git-job', jobId],
    queryFn: () => gitService.getJobStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.data && TERMINAL_JOB_STATUSES.has(query.state.data.job_status) ? false : 1500,
  });
}

/** @deprecated use useGitJobStatus */
export const usePushJobStatus = useGitJobStatus;


/** The tenant's source-repo config: which repo, which branch, and how changes reach it. */
export function useSourceRepo() {
  return useQuery<SourceRepoConfig>({
    queryKey: ['source-repo'],
    queryFn: () => gitService.getSourceRepo(),
    staleTime: 15_000,
  });
}

/** Connect or reconfigure the source repo. */
export function useSetSourceRepo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceRepoUpdate) => gitService.setSourceRepo(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-repo'] });
      queryClient.invalidateQueries({ queryKey: ['git-develop'] });
    },
  });
}

export function useDisconnectSourceRepo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => gitService.disconnectSourceRepo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-repo'] });
    },
  });
}

/** Kicks the background import job (source repo → Develop); returns the job id to poll. */
export function useImportSourceRepo() {
  return useMutation({
    mutationFn: () => gitService.importSourceRepo(),
  });
}

/**
 * Repos our GitHub App can see. Only fetched when asked (`enabled`), because it
 * calls GitHub and returns nothing useful until the App has been installed.
 */
export function useAvailableRepos(enabled: boolean) {
  return useQuery<GithubRepoOption[]>({
    queryKey: ['github-repos'],
    queryFn: () => gitService.getAvailableRepos(),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The one unacknowledged import failure worth interrupting someone over.
 *
 * Polled rather than pushed: a webhook import fails on a worker, with nobody
 * watching, so there is no request in flight for the answer to ride back on.
 * Sixty seconds is the compromise between noticing and nagging — a failed
 * import has already happened, and a minute's delay changes nothing about the
 * fix.
 */
export function useImportAlert(enabled: boolean) {
  return useQuery<{ alert: ImportRun | null }>({
    queryKey: ['git-import-alert'],
    queryFn: () => gitService.getImportAlert(),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
    // A tenant with no repo has no imports, and a 403 for a ReadOnly user is not
    // worth retrying every minute.
    retry: false,
  });
}

/** Recent import attempts, for the Git tab's history view. */
export function useImportHistory(enabled = true) {
  return useQuery<{ imports: ImportRun[] }>({
    queryKey: ['git-imports'],
    queryFn: () => gitService.listImports(),
    enabled,
    staleTime: 15_000,
  });
}

/** Dismiss one failure; the banner goes away until the next one. */
export function useAcknowledgeImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gitService.acknowledgeImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['git-import-alert'] });
      queryClient.invalidateQueries({ queryKey: ['git-imports'] });
    },
  });
}

/** Re-run a failed import from the banner's modal. */
export function useRetryImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gitService.retryImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['git-import-alert'] });
      queryClient.invalidateQueries({ queryKey: ['git-imports'] });
    },
  });
}
